import type { ReactNode } from "react";
import { ApiError } from "../../lib/api";
import { MicroLabel } from "./Text";

/**
 * The three states every view has besides "here is the data": loading, empty,
 * and failed. Centralised so no view invents its own — an application where each
 * page fails differently is one where operators learn to distrust all of them.
 *
 * Nothing here is a spinner over the whole page. A skeleton keeps the layout
 * stable, which means no shift when the data lands (`performance.md`: no layout
 * shifts from dynamic content).
 */

export function SkeletonRows({
  rows = 6,
  columns = 5,
}: {
  readonly rows?: number;
  readonly columns?: number;
}) {
  return (
    <tbody aria-hidden="true">
      {Array.from({ length: rows }, (_unused, rowIndex) => (
        <tr key={rowIndex} className="border-b border-rule">
          {Array.from({ length: columns }, (_ignored, columnIndex) => (
            <td key={columnIndex} className="py-2 pr-4">
              <span
                className="stage block h-3 bg-paper-sunken"
                style={{
                  ["--stage-index" as string]: rowIndex * columns + columnIndex,
                  // Varied widths so it reads as text, not as a progress bar.
                  width: `${45 + ((rowIndex * 7 + columnIndex * 13) % 45)}%`,
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

/**
 * An empty result is not a failure, and the copy has to say which kind of empty
 * it is: "no charge points match this filter" is actionable, "No data" is not.
 */
export function EmptyState({
  title,
  detail,
  action,
}: {
  readonly title: string;
  readonly detail?: string;
  readonly action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-3 border-t border-rule py-section">
      <MicroLabel>Nothing here</MicroLabel>
      <p className="m-0 max-w-prose text-section font-medium text-ink">{title}</p>
      {detail === undefined ? null : (
        <p className="m-0 max-w-prose text-body text-ink-muted">{detail}</p>
      )}
      {action}
    </div>
  );
}

/**
 * A failed request. The server's message is shown verbatim because spec 9
 * guarantees it carries no secret and is written for a human; anything else gets
 * the fallback the API client composed.
 *
 * A 403 is separated out. "Forbidden" is not a fault the operator can retry
 * their way out of, and offering a retry button for one trains people to mash it.
 *
 * A 5xx is separated out too, but only in the label: "Server error" tells the
 * operator the request was fine and the fault is worth reporting. What makes it
 * reportable is in the message the server sent — a reference that appears in the
 * server log, and outside production the exception itself.
 */
export function ErrorNotice({
  error,
  onRetry,
}: {
  readonly error: unknown;
  readonly onRetry?: () => void;
}) {
  const isForbidden = error instanceof ApiError && error.isForbidden;
  const isServerFault = error instanceof ApiError && error.isServerFault;
  const message = error instanceof Error ? error.message : "Something failed and did not say why.";

  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-2 border-l-2 border-signal bg-signal-wash px-4 py-3"
    >
      <MicroLabel className="text-signal-ink">
        {isForbidden ? "Not permitted" : isServerFault ? "Server error" : "Request failed"}
      </MicroLabel>
      <p className="m-0 max-w-prose text-body text-signal-ink">{message}</p>
      {onRetry !== undefined && !isForbidden ? (
        <button
          type="button"
          onClick={onRetry}
          className="text-micro font-semibold uppercase text-signal underline decoration-1 underline-offset-4 hover:no-underline"
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}

/**
 * Announces the result of a mutation. `role="status"` so it is read out without
 * stealing focus, which matters when the action was a remote command and the
 * operator is watching the station rather than the screen.
 */
export function Notice({
  tone,
  children,
}: {
  /**
   * `warn` is for what is *about* to happen, and it earns a third tone rather than
   * borrowing `error`'s: the price-change form uses it to say how many stations one
   * button is about to reprice, and a caution rendered in the refusal colour teaches
   * an operator to read the refusal colour as decoration.
   */
  readonly tone: "ok" | "warn" | "error";
  readonly children: ReactNode;
}) {
  return (
    <div role="status" className={["border-l-2 px-4 py-2 text-body", TONE[tone]].join(" ")}>
      {children}
    </div>
  );
}

const TONE: Record<"ok" | "warn" | "error", string> = {
  ok: "border-state-available bg-state-available-wash text-state-available",
  warn: "border-state-transitional bg-state-transitional-wash text-ink",
  error: "border-signal bg-signal-wash text-signal-ink",
};
